import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Bell, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import NotificationRow from "./NotificationRow";
import { useMarkRead, useNotificationFeed } from "./useNotifications";

const PREVIEW_COUNT = 4;

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data: items = [] } = useNotificationFeed({ refetchInterval: 60000 });
  const { markRead, markAllRead } = useMarkRead();

  const unread = items.filter((i) => !i.read).length;

  const openItem = (item) => {
    markRead(item);
    setOpen(false);
    navigate(item.actionUrl);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-button"
          aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
          className={cn("relative p-2.5 rounded-full transition-colors", open ? "bg-secondary" : "hover:bg-secondary")}
        >
          <Bell className="h-5 w-5 text-foreground" strokeWidth={1.75} />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute top-0.5 right-0.5 h-[18px] min-w-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-background"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="notification-panel"
        align="end"
        alignOffset={-12}
        sideOffset={10}
        collisionPadding={12}
        className="w-[min(26rem,calc(100vw-1.5rem))] p-0 rounded-2xl border-border bg-card shadow-[0_18px_40px_-12px_rgba(42,33,29,0.25)] overflow-hidden"
      >
        <PopoverPrimitive.Arrow width={18} height={9} className="fill-card" />
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3.5 border-b border-border">
          <h2 className="font-serif text-xl text-foreground">Notifications</h2>
          {unread > 0 && (
            <button data-testid="notification-mark-all-read" onClick={markAllRead} className="text-sm font-medium text-accent hover:underline">
              Mark all as read
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="px-6 py-10 text-center">
            <span className="mx-auto mb-3 h-12 w-12 rounded-full bg-success/10 text-success flex items-center justify-center">
              <CheckCheck className="h-5 w-5" />
            </span>
            <p className="text-sm font-medium text-foreground">You're all caught up</p>
            <p className="text-xs text-muted-foreground mt-1">New approvals, AI previews and follow-ups will show up here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border max-h-[min(26rem,60vh)] overflow-y-auto">
            {items.slice(0, PREVIEW_COUNT).map((item, idx) => (
              <li key={item.key || item.id}>
                <NotificationRow item={item} onOpen={openItem} testId={`notification-item-${idx}`} />
              </li>
            ))}
          </ul>
        )}

        <div className="p-3 border-t border-border">
          <button
            data-testid="notification-view-all"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className="w-full rounded-xl bg-accent/10 hover:bg-accent/15 py-2.5 text-sm font-medium text-accent transition-colors"
          >
            View all notifications
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
